/**
 * "Add a test" — a test case for ONE screen, written by hand in plain words.
 *
 * Opened from the screen's test window (workbench › Check). A person picks
 * steps ("Click a button", "Type in a field") and checks ("A message
 * appears") from the screen's own fields and buttons; each step reads back as
 * the sentence the Testing tool shows. The result is an ordinary case file
 * (`origin: user`), validated on save like every other case, run on its own
 * with "Save and run" and included in the screen's full test from then on.
 *
 * Which steps are offered and what each asks for is DATA
 * (`caseBuilderSteps.json`); the screen's choices come from
 * `GET /ui-testing/{project}/case-builder?obj=`. A step this builder does not
 * offer (an AI-written wizard step, say) is kept as written and shown in
 * words; "Edit as JSON" opens the specialist's editor on the same case.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import toast from 'react-hot-toast'
import { ArrowDown, ArrowUp, Braces, Play, Plus, Save, Trash2, X } from 'lucide-react'
import Modal from '@/components/common/Modal'
import {
  apiError, apiIssues, getCase, getCaseBuilder, saveCase, startRun,
  type BuilderColumn, type CaseBuilderVocabulary, type Issue, type TestCase,
} from '@/api/uiTesting'
import CaseEditor from './CaseEditor'
import { IssuesTable, inputStyle, labelCaps, mutedText } from './shared'
import { actionLabel, caseTypeText, fieldStateText, isCheck, nice, personaText, stepText, valueText } from './plainSteps'
import catalog from './caseBuilderSteps.json'

type Step = Record<string, unknown>

interface ParamDef {
  key: string
  kind: string
  label: string
  optional?: boolean
  default?: unknown
  filter?: string
  tokens?: string[]
  options?: string
}

interface StepDef {
  name: string
  label: string
  params: ParamDef[]
  fixed?: Record<string, string>
  screenTypes?: string[]
}

const SCREEN = '@screen'
const DO_STEPS = catalog.do as unknown as StepDef[]
const EXPECT_STEPS = catalog.expect as unknown as StepDef[]
const CATALOG_OPTIONS = catalog.options as Record<string, string[]>

const defOf = (s: Step): StepDef | undefined => (isCheck(s)
  ? EXPECT_STEPS.find((d) => d.name === s.expect)
  : DO_STEPS.find((d) => d.name === s.do))

const forScreen = (defs: StepDef[], objType: string) =>
  defs.filter((d) => !d.screenTypes || d.screenTypes.includes(objType))

/** `@screen` in the catalog → the screen's own name. */
function bind(step: Record<string, unknown>, obj: string): Step {
  return Object.fromEntries(Object.entries(step).map(([k, v]) => [k, v === SCREEN ? obj : v]))
}

const sameObj = (a: unknown, b: string) => String(a ?? '').toLowerCase() === b.toLowerCase()

const field: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160, flex: '1 1 180px' }
const control: CSSProperties = { ...inputStyle, minHeight: 34, width: '100%' }
const fieldLabel: CSSProperties = { fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }

const upperFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export default function CaseBuilder({ project, obj, caseId, onClose, onSaved, onRan }: {
  project: string
  obj: { obj_type: string; obj_name: string; title: string }
  /** Edit this case; absent ⇒ a new one. */
  caseId?: string
  onClose: () => void
  /** The case file changed (saved). */
  onSaved?: () => void
  /** "Save and run" started this run. */
  onRan?: (runId: string) => void
}) {
  const [vocab, setVocab] = useState<CaseBuilderVocabulary | null>(null)
  const [draft, setDraft] = useState<TestCase | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [issues, setIssues] = useState<Issue[]>([])
  const [busy, setBusy] = useState<'save' | 'run' | null>(null)
  const [json, setJson] = useState(false)
  // Read in the JSON editor's onClose, which runs in the same tick as its save.
  const jsonSaved = useRef(false)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const v = await getCaseBuilder(project, obj.obj_name)
        const c = caseId ? await getCase(project, caseId) : null
        if (!alive) return
        setVocab(v)
        setDraft(c ?? {
          id: v.next_id,
          title: '',
          object: v.obj,
          surface: 'app',
          type: 'positive',
          priority: 'medium',
          persona: 'owner',
          origin: 'user',
          requirement: [],
          steps: ((catalog.starter as Record<string, Step[]>)[v.obj_type] ?? catalog.starter['*'])
            .map((s) => bind(s, v.obj)),
        })
      } catch (err) {
        console.error('[case-builder] could not load', err)
        if (alive) setLoadError(apiError(err, caseId ? 'Could not load the test' : 'Could not load what this screen has'))
      }
    })()
    return () => { alive = false }
  }, [project, obj.obj_name, caseId])

  const steps = useMemo(() => ((draft?.steps as Step[] | undefined) ?? []), [draft])
  const pre = useMemo(() => ((draft?.preconditions as Step[] | undefined) ?? []), [draft])
  const seedFirst = catalog.seedFirst as Step
  const seeded = pre.some((s) => s.do === seedFirst.do && s.save_as === seedFirst.save_as)
  const objType = vocab?.obj_type ?? obj.obj_type
  const doOptions = forScreen(DO_STEPS, objType)
  const expectOptions = forScreen(EXPECT_STEPS, objType)

  const patch = (p: Partial<TestCase>) => setDraft((d) => (d ? { ...d, ...p } : d))
  const setSteps = (next: Step[]) => patch({ steps: next })

  /** The tab on screen before step `i` (open_screen / open_tab set it). */
  const formAt = (i: number): string => {
    let form = vocab?.forms[0]?.form_no ?? ''
    for (const s of steps.slice(0, i)) {
      if (s.do === 'open_screen') form = String(s.form || vocab?.forms[0]?.form_no || '')
      else if (s.do === 'open_tab' && s.form) form = String(s.form)
    }
    return form
  }

  /** Names a record was saved under before step `i`. */
  const savedAt = (i: number): string[] => {
    const out: string[] = []
    for (const s of [...pre, ...steps.slice(0, i)]) {
      if ((s.do === 'seed_record' || s.expect === 'record') && s.save_as) out.push(String(s.save_as))
    }
    return Array.from(new Set(out))
  }

  const columnsOf = (formNo: string, filter?: string): BuilderColumn[] => {
    const form = vocab?.forms.find((f) => f.form_no === formNo) ?? vocab?.forms[0]
    const cols = (form?.columns ?? []).filter((c) => !c.hidden)
    const picked = filter === 'enterable' ? cols.filter((c) => c.enterable)
      : filter === 'lookup' ? cols.filter((c) => c.lookup)
        : filter === 'choices' ? cols.filter((c) => c.choices.length > 0)
          : cols
    return picked.length ? picked : cols
  }

  const optionsOf = (name?: string): string[] => {
    if (!name) return []
    const fromVocab = vocab ? (vocab as unknown as Record<string, unknown>)[name] : undefined
    return Array.isArray(fromVocab) ? fromVocab.map(String) : CATALOG_OPTIONS[name] ?? []
  }

  const addStep = (def: StepDef, check: boolean) => {
    if (!vocab) return
    const i = steps.length
    const step: Step = { [check ? 'expect' : 'do']: def.name, ...bind(def.fixed ?? {}, vocab.obj) }
    for (const p of def.params) {
      let v: unknown
      if (p.kind === 'field') v = columnsOf(formAt(i), p.filter)[0]?.key
      else if (p.kind === 'form') v = p.optional ? undefined : formAt(i) || vocab.forms[0]?.form_no
      else if (p.kind === 'saved') v = savedAt(i)[0]           // the record created first, when there is one
      else if (p.kind === 'choice') v = p.default ?? (p.optional ? undefined : optionsOf(p.options)[0])
      else v = p.default
      if (v !== undefined && v !== '') step[p.key] = v
    }
    // A value for a list field: its first choice, not a token it cannot take.
    if (def.name === 'select' && step.field) {
      const col = columnsOf(formAt(i)).find((c) => c.key === step.field)
      if (col?.choices.length) step.value = col.choices[0]
    }
    setSteps([...steps, step])
  }

  const setParam = (i: number, key: string, v: unknown) => {
    const next = steps.map((s, n) => {
      if (n !== i) return s
      const out = { ...s }
      if (v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v))) delete out[key]
      else out[key] = v
      return out
    })
    setSteps(next)
  }

  const move = (i: number, by: number) => {
    const j = i + by
    if (j < 0 || j >= steps.length) return
    const next = [...steps]
    ;[next[i], next[j]] = [next[j], next[i]]
    setSteps(next)
  }

  const toggleSeed = (on: boolean) => {
    if (!vocab) return
    const rest = pre.filter((s) => !(s.do === seedFirst.do && s.save_as === seedFirst.save_as))
    patch({ preconditions: on ? [bind(seedFirst, vocab.obj), ...rest] : rest })
  }

  /** The case as it is saved: no empty preconditions, always a person's. */
  const finished = (): TestCase | null => {
    if (!draft || !vocab) return null
    const out: TestCase = { ...draft, object: draft.object || vocab.obj, surface: draft.surface || 'app', origin: 'user' }
    if (!pre.length) delete out.preconditions
    return out
  }

  const save = async (andRun: boolean) => {
    const c = finished()
    if (!c) return
    if (!String(c.title ?? '').trim()) {
      setIssues([{ case: c.id, where: 'case', severity: 'error', message: 'Give the test a title — what does it prove?' }])
      return
    }
    setBusy(andRun ? 'run' : 'save')
    setIssues([])
    let warnings: Issue[] = []
    try {
      const r = await saveCase(project, c.id, c)
      warnings = r.warnings
      onSaved?.()
    } catch (err) {
      setIssues(apiIssues(err))
      toast.error(apiError(err, 'Could not save the test'))
      setBusy(null)
      return
    }
    if (!andRun) {
      setBusy(null)
      if (warnings.length) {
        setIssues(warnings)
        toast.success(`Saved with ${warnings.length} warning(s)`)
      } else {
        toast.success('Test saved')
        onClose()
      }
      return
    }
    try {
      const run = await startRun(project, { trace: 'failed', case_ids: [c.id] })
      toast.success('Saved — running the test')
      onRan?.(run.run_id)
    } catch (err) {
      console.error('[case-builder] run did not start', err)
      toast.error(apiError(err, 'Saved, but the test could not start'))
    } finally {
      setBusy(null)
    }
  }

  const issuesAt = (where: string) => issues.filter((x) => x.where === where)
  const otherIssues = issues.filter((x) => !/^step \d+$/.test(x.where))

  if (json && draft) {
    return (
      <CaseEditor project={project} caseId={draft.id} initial={finished() ?? draft} canTest
        onChanged={() => { jsonSaved.current = true; onSaved?.() }}
        onClose={() => { if (jsonSaved.current) onClose(); else setJson(false) }} />
    )
  }

  const title = caseId ? `Edit test — ${obj.title}` : `Add a test — ${obj.title}`
  return (
    <Modal open size="wide" onClose={() => { if (!busy) onClose() }} title={title}
      footer={<>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={!!busy}>Cancel</button>
        <button type="button" className="btn btn-ghost" disabled={!draft || !!busy} onClick={() => setJson(true)}
          title="For specialists: the case file behind this test" data-tw-action="case-json">
          <Braces size={14} /> Edit as JSON
        </button>
        <button type="button" className="btn btn-secondary" disabled={!draft || !!busy} data-tw-action="case-save"
          onClick={() => void save(false)}>
          <Save size={14} /> {busy === 'save' ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn btn-primary" disabled={!draft || !!busy} data-tw-action="case-save-run"
          onClick={() => void save(true)}>
          <Play size={14} /> {busy === 'run' ? 'Starting…' : 'Save and run'}
        </button>
      </>}>
      {loadError ? <p style={{ color: 'var(--color-error)', margin: 0 }}>{loadError}</p>
        : !draft || !vocab ? <div className="spinner" style={{ margin: '1.5rem auto' }} />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ ...mutedText, margin: 0, fontSize: 14, lineHeight: 1.5 }}>
                Build the test the way you would try the screen: the steps to take, then what you expect to see.
                It runs in a real browser; from now on the screen’s full test includes it.
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <label style={{ ...field, flex: '3 1 320px' }}>
                  <span style={fieldLabel}>What does this test prove?</span>
                  <input style={control} value={String(draft.title ?? '')} data-tw-field="case-title"
                    placeholder="e.g. An order without a customer is refused"
                    onChange={(e) => patch({ title: e.target.value })} />
                </label>
                <label style={field}>
                  <span style={fieldLabel}>It</span>
                  <select style={control} value={String(draft.type ?? 'positive')} data-tw-field="case-type"
                    onChange={(e) => patch({ type: e.target.value })}>
                    {vocab.case_types.map((t) => <option key={t} value={t}>{caseTypeText(t)}</option>)}
                  </select>
                </label>
                <label style={field}>
                  <span style={fieldLabel}>Run it</span>
                  <select style={control} value={String(draft.persona ?? 'owner')} data-tw-field="case-persona"
                    onChange={(e) => patch({ persona: e.target.value })}>
                    {Array.from(new Set([...vocab.personas, String(draft.persona ?? 'owner')])).map((p) => (
                      <option key={p} value={p}>{personaText(p)}</option>
                    ))}
                  </select>
                </label>
              </div>

              {objType === 'T' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                  <input type="checkbox" checked={seeded} onChange={(e) => toggleSeed(e.target.checked)}
                    data-tw-field="case-seed-first" />
                  Before it starts, create a {nice(vocab.title).toLowerCase()} record to work with
                  <span style={mutedText}>(for tests that edit, act on or delete a record)</span>
                </label>
              )}
              {pre.filter((s) => !(s.do === seedFirst.do && s.save_as === seedFirst.save_as)).length > 0 && (
                <p style={{ ...mutedText, margin: 0 }}>
                  Also before it starts: {pre.filter((s) => !(s.do === seedFirst.do && s.save_as === seedFirst.save_as))
                    .map((s) => stepText(s)).join('; ')} (kept as written).
                </p>
              )}

              <div style={labelCaps}>Steps</div>
              {steps.length === 0 && <p style={{ ...mutedText, margin: 0 }}>No steps yet — add one below.</p>}
              <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {steps.map((s, i) => (
                  <StepRow key={i} n={i + 1} step={s} def={defOf(s)} count={steps.length}
                    issues={issuesAt(`step ${i + 1}`)}
                    columns={(filter) => columnsOf(formAt(i), filter)}
                    formAt={formAt(i)} saved={savedAt(i)} vocab={vocab} optionsOf={optionsOf}
                    onParam={(k, v) => setParam(i, k, v)}
                    onUp={() => move(i, -1)} onDown={() => move(i, 1)}
                    onRemove={() => setSteps(steps.filter((_, n) => n !== i))} />
                ))}
              </ol>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <AddStep label="Add a step…" defs={doOptions} onAdd={(d) => addStep(d, false)} hook="case-add-step" />
                <AddStep label="Add a check…" defs={expectOptions} onAdd={(d) => addStep(d, true)} hook="case-add-check" />
              </div>
              {!steps.some(isCheck) && (
                <p style={{ ...mutedText, margin: 0 }}>
                  A test must check something — add at least one check (for example “A message appears”).
                </p>
              )}

              {otherIssues.length > 0 && <IssuesTable issues={otherIssues} />}
              <p style={{ ...mutedText, margin: 0 }}>Saved as <code>{draft.id}</code></p>
            </div>
          )}
    </Modal>
  )
}

function AddStep({ label, defs, onAdd, hook }: {
  label: string; defs: StepDef[]; onAdd: (d: StepDef) => void; hook: string
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Plus size={15} aria-hidden="true" />
      <select style={{ ...control, width: 'auto', minWidth: 220 }} value="" aria-label={label} data-tw-field={hook}
        onChange={(e) => {
          const d = defs.find((x) => x.name === e.target.value)
          if (d) onAdd(d)
        }}>
        <option value="">{label}</option>
        {defs.map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
      </select>
    </label>
  )
}

function StepRow({ n, step, def, count, issues, columns, formAt, saved, vocab, optionsOf, onParam, onUp, onDown, onRemove }: {
  n: number
  step: Step
  def?: StepDef
  count: number
  issues: Issue[]
  columns: (filter?: string) => BuilderColumn[]
  formAt: string
  saved: string[]
  vocab: CaseBuilderVocabulary
  optionsOf: (name?: string) => string[]
  onParam: (key: string, v: unknown) => void
  onUp: () => void
  onDown: () => void
  onRemove: () => void
}) {
  const check = isCheck(step)
  const columnOf = (key: unknown) => columns().find((c) => c.key === key || c.db_name.toLowerCase() === String(key ?? '').toLowerCase())
  return (
    <li style={{
      border: `1px solid ${issues.some((x) => x.severity === 'error') ? 'var(--color-error)' : 'var(--color-border)'}`,
      borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8,
    }} data-tw-row={`case-step-${n}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          flex: 'none', minWidth: 24, height: 24, borderRadius: 12, display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 12, fontWeight: 600,
          background: check ? 'var(--color-success-weak, var(--color-bg-secondary))' : 'var(--color-accent-weak)',
          color: check ? 'var(--color-success)' : 'var(--color-accent)',
        }}>{n}</span>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>
          {check && <span style={{ ...mutedText, marginRight: 6 }}>Check:</span>}
          {stepText(step)}
        </span>
        <button type="button" className="btn btn-ghost btn-sm" aria-label="Move up" disabled={n === 1} onClick={onUp}><ArrowUp size={14} /></button>
        <button type="button" className="btn btn-ghost btn-sm" aria-label="Move down" disabled={n === count} onClick={onDown}><ArrowDown size={14} /></button>
        <button type="button" className="btn btn-ghost btn-sm" aria-label="Remove the step" onClick={onRemove}><Trash2 size={14} /></button>
      </div>
      {!def ? (
        <p style={{ ...mutedText, margin: 0 }}>Kept as written — use Edit as JSON to change this step.</p>
      ) : def.params.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {def.params.map((p) => (
            <label key={p.key} style={p.kind === 'where' ? { ...field, flex: '1 1 100%' } : field}>
              <span style={fieldLabel}>{p.label}</span>
              <ParamInput p={p} value={step[p.key]} columns={columns} formAt={formAt} saved={saved}
                vocab={vocab} optionsOf={optionsOf} column={columnOf(step.field)}
                onChange={(v) => onParam(p.key, v)} />
            </label>
          ))}
        </div>
      )}
      {issues.map((x, k) => (
        <div key={k} style={{ fontSize: 13, color: x.severity === 'error' ? 'var(--color-error)' : 'var(--color-warning)' }}>
          {x.message}
        </div>
      ))}
    </li>
  )
}

function ParamInput({ p, value, columns, formAt, saved, vocab, optionsOf, column, onChange }: {
  p: ParamDef
  value: unknown
  columns: (filter?: string) => BuilderColumn[]
  formAt: string
  saved: string[]
  vocab: CaseBuilderVocabulary
  optionsOf: (name?: string) => string[]
  column?: BuilderColumn
  onChange: (v: unknown) => void
}) {
  const str = value == null ? '' : String(value)
  switch (p.kind) {
    case 'form':
      return (
        <select style={control} value={str} onChange={(e) => onChange(e.target.value)}>
          {p.optional && <option value="">The first tab</option>}
          {vocab.forms.map((f) => <option key={f.form_no} value={f.form_no}>{f.title}</option>)}
        </select>
      )
    case 'field': {
      const cols = columns(p.filter)
      if (!cols.length) return <input style={control} value={str} onChange={(e) => onChange(e.target.value)} />
      const known = cols.some((c) => c.key === str)
      return (
        <select style={control} value={str} onChange={(e) => onChange(e.target.value)}>
          {!known && str && <option value={str}>{nice(str)}</option>}
          {cols.map((c) => (
            <option key={c.key} value={c.key}>{c.heading}{c.mandatory ? ' *' : ''}</option>
          ))}
        </select>
      )
    }
    case 'action': {
      const own = vocab.actions.filter((a) => !a.form_no || a.form_no === formAt || !formAt)
      const names = new Set([...own.map((a) => a.name), ...vocab.builtin_actions])
      return (
        <select style={control} value={str} onChange={(e) => onChange(e.target.value)}>
          {!names.has(str) && str && <option value={str}>{actionLabel(str)}</option>}
          {own.length > 0 && (
            <optgroup label="This screen’s buttons">
              {own.map((a) => <option key={`m-${a.name}`} value={a.name}>{actionLabel(a.name)}</option>)}
            </optgroup>
          )}
          <optgroup label="Every screen">
            {vocab.builtin_actions.map((a) => <option key={`b-${a}`} value={a}>{actionLabel(a)}</option>)}
          </optgroup>
        </select>
      )
    }
    case 'value':
      return <ValueInput value={value} tokens={p.tokens ?? vocab.tokens} optional={p.optional}
        choices={column?.choices ?? []} onChange={onChange} />
    case 'number':
      return (
        <input style={control} type="number" value={str} min={0}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />
      )
    case 'bool':
      return (
        <input type="checkbox" checked={value === true} style={{ alignSelf: 'flex-start', marginTop: 8 }}
          onChange={(e) => onChange(e.target.checked ? true : undefined)} />
      )
    case 'choice': {
      const opts = optionsOf(p.options)
      const label = (o: string) => (p.options === 'field_states' ? upperFirst(fieldStateText(o))
        : p.options === 'message_kinds' ? nice(o) : o.toUpperCase())
      return (
        <select style={control} value={str} onChange={(e) => onChange(e.target.value)}>
          {p.optional && <option value="">Any</option>}
          {!opts.includes(str) && str && <option value={str}>{str}</option>}
          {opts.map((o) => <option key={o} value={o}>{label(o)}</option>)}
        </select>
      )
    }
    case 'saved':
      return (
        <select style={control} value={str} disabled={!saved.length && !str} onChange={(e) => onChange(e.target.value)}>
          <option value="">{saved.length ? 'None' : 'None created first'}</option>
          {Array.from(new Set([...saved, ...(str ? [str] : [])])).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      )
    case 'where': {
      // A record is matched on its HEADER columns (what the record check reads).
      const header = (vocab.forms[0]?.columns ?? []).filter((c) => !c.hidden)
      return <WhereInput value={value} columns={header} onChange={onChange} />
    }
    default:
      return <input style={control} value={str} onChange={(e) => onChange(e.target.value)} />
  }
}

const LITERAL = '__literal__'

/** A literal, or a run-time token ("a valid value", "today's date"…). */
function ValueInput({ value, tokens, optional, choices, onChange }: {
  value: unknown; tokens: string[]; optional?: boolean; choices: string[]; onChange: (v: unknown) => void
}) {
  const str = value == null ? '' : String(value)
  const isToken = str.startsWith('@')
  const offered = Array.from(new Set([...tokens, ...(isToken ? [str] : [])]))
  const mode = value == null || str === '' ? (optional ? '' : LITERAL) : isToken ? str : LITERAL
  return (
    <span style={{ display: 'flex', gap: 6 }}>
      {(offered.length > 0 || optional) && (
        <select style={{ ...control, flex: '1 1 50%' }} value={mode}
          onChange={(e) => {
            const m = e.target.value
            if (m === '') onChange(undefined)
            else if (m === LITERAL) onChange(choices[0] ?? (isToken ? '' : str))
            else onChange(m)
          }}>
          {optional && <option value="">Any value</option>}
          <option value={LITERAL}>This value…</option>
          {offered.map((t) => <option key={t} value={t}>{upperFirst(valueText(t))}</option>)}
        </select>
      )}
      {mode === LITERAL && (choices.length > 0 ? (
        <select style={{ ...control, flex: '1 1 50%' }} value={str} onChange={(e) => onChange(e.target.value)}>
          {!choices.includes(str) && <option value={str}>{str || '—'}</option>}
          {choices.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      ) : (
        <input style={{ ...control, flex: '1 1 50%' }} value={isToken ? '' : str} placeholder="Type the value"
          onChange={(e) => onChange(e.target.value)} />
      ))}
    </span>
  )
}

/** column → value pairs (`where` of a record check). */
function WhereInput({ value, columns, onChange }: {
  value: unknown; columns: BuilderColumn[]; onChange: (v: unknown) => void
}) {
  const columnOf = (k: string) => columns.find((c) => c.db_name === k || c.key === k)
  const pairs = Object.entries((value && typeof value === 'object' ? value : {}) as Record<string, unknown>)
  const set = (next: [string, unknown][]) => onChange(next.length ? Object.fromEntries(next) : undefined)
  const unused = columns.filter((c) => !pairs.some(([k]) => k === c.db_name || k === c.key))
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {pairs.map(([k, v], i) => (
        <span key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <select style={{ ...control, flex: '1 1 40%' }} value={k}
            onChange={(e) => set(pairs.map((x, n) => (n === i ? [e.target.value, x[1]] : x)))}>
            {!columns.some((c) => c.db_name === k) && <option value={k}>{nice(k)}</option>}
            {columns.map((c) => <option key={c.db_name} value={c.db_name}>{c.heading}</option>)}
          </select>
          <span style={{ flex: '1 1 60%' }}>
            <ValueInput value={v} tokens={['@filled', '@today']} choices={columnOf(k)?.choices ?? []}
              onChange={(nv) => set(pairs.map((x, n) => (n === i ? [x[0], nv ?? ''] : x)))} />
          </span>
          <button type="button" className="btn btn-ghost btn-sm" aria-label="Remove"
            onClick={() => set(pairs.filter((_, n) => n !== i))}><X size={14} /></button>
        </span>
      ))}
      {unused.length > 0 && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
          onClick={() => set([...pairs, [unused[0].db_name, '@filled']])}>
          <Plus size={14} /> Add a field to match
        </button>
      )}
    </span>
  )
}
